/**
 * UserMenuDesktop Component
 *
 * Displays a desktop user menu with authenticated user information and account actions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.switchE1E2DestinationForm - Callback function to open the E1/E2 destination form submission
 * @param {Function} props.logout - Callback function to handle user logout
 * @returns {JSX.Element} A div containing the user avatar, name, email, and action menu
 *
 * @example
 * <UserMenuDesktop
 *   switchE1E2DestinationForm={() => setShowForm(true)}
 *   logout={handleLogout}
 * />
 *
 * @remarks
 * - Requires Okta authentication context to be available via @okta/okta-react
 * - Displays user's name and email extracted from Okta ID token
 * - Shows an avatar icon and provides options to submit E1/E2 destination or logout
 * - Uses CSS modules for styling (UserMenu.module.scss)
 */

import { useMemo } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import styles from './scss/UserMenu.module.scss'

export default function UserMenuDesktop({ switchE1E2DestinationForm, logout }) {
  const { authState } = useOktaAuth()

  const [userName, userEmail] = useMemo(
    () =>
      authState?.isAuthenticated
        ? [authState?.idToken?.claims?.name, authState?.idToken?.claims?.email]
        : ['', ''],
    [authState]
  )

  return (
    <div className={styles.userMenuDesktop}>
      <div>
        <div className={styles.userAvatar}>
          <i className={styles.userIcon} />
        </div>

        <div>
          <span title={userName}>{userName}</span>
          <span title={userEmail}>{userEmail}</span>
        </div>
      </div>

      <ul className={styles.userMenuList}>
        <li onClick={() => switchE1E2DestinationForm()}>Submit E1/E2 Destination</li>
        <li onClick={() => logout()}>Logout</li>
      </ul>
    </div>
  )
}
